import { AvailableLanguages, TAvailableSurveyLanguages } from "./locale";
import { SurveyQuestionResponse } from "./question";

export interface Survey {
  id: string;
  title: string;
}

export enum SurveyDispatchStatus {
  "InProgress" = "InProgress",
  "Ready" = "Ready",
  "Active" = "Active",
  "Latest" = "Latest",
  "Past" = "Past",
}

export interface SurveyDispatchReminderResponse {
  id: number | null;
  date: string;
  is_final: boolean;
}

export interface SurveyDispatchReminder {
  id: number | null;
  tempId?: number; // Used to identify the new reminder in the UI
  date?: Date;
  isFinal: boolean;
}

export interface GetSurveyDispatchesResponse {
  created_at: string;
  due_date: string | null;
  engagement: number;
  has_job_roles: boolean;
  id: string;
  is_public: boolean;
  launch_date: string | null;
  name: string;
  recipients_count: number;
  reminders: SurveyDispatchReminderResponse[];
  survey_id: string;
  total_score: number | null;
}

export interface SurveyRecipient {
  email: string;
  phone_number?: string;
}

export interface GetSurveyDispatchResponse extends GetSurveyDispatchesResponse {
  recipients: SurveyRecipient[];
}

export interface UpdateSurveyDispatchBody {
  launch_date?: string;
  due_date?: string;
  reminders?: SurveyDispatchReminderResponse[];
  recipients?: SurveyRecipient[];
}

export interface InvalidRecipient {
  email?: string;
  phone_number?: string;
  error_type: "INVALID_EMAIL" | "INVALID_PHONE_NUMBER" | "ALREADY_IN_LIST";
}

export interface CreateSurveyDispatchResponse {
  invalid_recipients?: InvalidRecipient[];
  survey_dispatch: GetSurveyDispatchResponse;
}

export interface GetSurveyResponse {
  reference_id: string;
  chapters: SurveyChapterResponse[];
  due_date: string;
  is_public?: boolean;
  title: string;
}

export interface PreviewSurveyResponse {
  chapters: SurveyChapterResponse[];
  due_date: string;
  is_public: boolean;
  reference_id: string;
  title: string;
}

export interface SurveyAnswer {
  question: string;
  options: string[];
}

export interface SubmitSurveyBody {
  answers: SurveyAnswer[] | undefined;
}

export interface SurveyChapterResponse {
  reference_id: string;
  title: string;
  order: number;
  description?: string | undefined;
  trigger_warning?: string | undefined;
  questions: SurveyQuestionResponse[];
  image?: string | undefined;
  hidden: boolean;
}

export interface CustomQuestionAddBody {
  text: string;
  label: string;
  options: ICustomQuestionOption[];
}

export interface CustomQuestionUpdateBody {
  text: string;
  label: string;
  options: ICustomQuestionOption[];
}

export interface CustomQuestion {
  id: string;
  text: string;
  label: string;
  dispatch_ids: string[];
  options: ICustomQuestionOption[];
}

export interface ICustomQuestionOption {
  id: string | null;
  text: string;
  label: string;
}

export type GetSurveyJobLevels = Array<{
  name: string;
  job_level: string;
  order: number;
}>;

export type GetJobRolesResponse = Array<{
  id: string;
  job_level: string;
  name: string;
  order: number;
  dispatch_ids: string[];
}>;

export interface UpdateJobRoleBody {
  id?: string;
  job_level: string;
  name: string;
  order?: number;
}

export interface JobRole {
  id?: string;
  tempId?: string;
  jobLevel: string;
  name: string;
  order: number;
  dispatchIds?: string[];
}

export enum SurveyErrorCodes {
  CLOSED = "SURVEY_CLOSED",
  INVALID = "INVITE_TOKEN_INVALID",
  ALREADY_USED = "TOKEN_ALREADY_USED",
}

export interface SurveyEmailLanguage {
  html_body: string;
  language_code: AvailableLanguages;
  name: string;
  native_name: string;
  subject: string;
}

export interface SurveyEmailContent {
  html_body: string;
  language_code: TAvailableSurveyLanguages;
  order: number;
  subject: string;
  text_body: string;
}

export interface SurveyEmailResponse {
  content: SurveyEmailContent[];
  custom_content: string;
  dispatch_id: string;
  reminder_id: string;
  survey_email_id: string;
  type: string;
}

// Body in post request for updating survey email
export interface SurveyEmailBody {
  content: {
    language_code: AvailableLanguages;
    order?: number;
  }[];
  custom_content?: string;
  survey_email_id: string;
}

export interface InvalidRespondentsResponse {
  invalid_respondents_count: number;
  errors: {
    count: number;
    error_str: string;
    error_type: string;
  }[];
}
